import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { House, Path, Gift, Trophy, ShoppingCart, User, SignOut, Wallet, ChartLine, Medal, ShieldCheck, Motorcycle, Users, Clock, ChatDots, Question, Cpu, Crown, VideoCamera, Storefront, Sun, Moon, Bell, Tote, ChatCircle, Lightning } from '@phosphor-icons/react';
import { useTheme } from '../context/ThemeContext';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';

const navItems = [
    { path: '/dashboard', icon: House, label: 'Home' },
    { path: '/ride', icon: Path, label: 'Ride' },
    { path: '/garage', icon: Motorcycle, label: 'Garage' },
    { path: '/controller', icon: Cpu, label: 'Tune' },
    { path: '/community', icon: Users, label: 'Feed' },
    { path: '/challenges', icon: Trophy, label: 'Quests' },
    { path: '/shop', icon: ShoppingCart, label: 'BTR' },
    { path: '/governance', icon: Crown, label: 'DAO' },
];

export default function TopNav() {
    const { user, logout, isAdmin } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();

    const getInitials = () => {
        if (!user) return 'U';
        if (user.first_name) {
            return user.first_name.charAt(0) + (user.last_name?.charAt(0) || '');
        }
        return user.email?.charAt(0).toUpperCase() || 'U';
    };

    return (
        <header className="glass-nav sticky top-0 z-50" data-testid="top-navigation">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="flex items-center justify-between h-16">
                    <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
                        <img src="/boltorium_graffiti.png" alt="Boltorium" className="h-9 w-auto brightness-110" />
                    </Link>

                    <nav className="hidden md:flex items-center gap-1">
                        {navItems.map(({ path, icon: Icon, label }) => {
                            const isActive = location.pathname === path ||
                                (path === '/shop' && location.pathname.startsWith('/shop'));

                            return (
                                <Link
                                    key={path}
                                    to={path}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold tracking-wider uppercase transition-all duration-300 ${
                                        isActive
                                            ? 'bg-primary/15 text-primary text-glow'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                                    }`}
                                    style={{ fontFamily: 'var(--font-display)', fontSize: '0.7rem' }}
                                    data-testid={`desktop-nav-${label.toLowerCase()}`}
                                >
                                    <Icon size={18} weight={isActive ? 'fill' : 'regular'} />
                                    {label}
                                </Link>
                            );
                        })}
                    </nav>

                    <div className="flex items-center gap-3">
                        {/* Chat icon */}
                        <button
                            onClick={() => navigate('/chat')}
                            className="p-1.5 rounded-full hover:bg-white/5 transition-colors text-muted-foreground hover:text-foreground relative"
                            data-testid="chat-nav-btn"
                        >
                            <ChatCircle size={18} />
                        </button>

                        {/* Cart icon */}
                        <button
                            onClick={() => navigate('/cart')}
                            className="p-1.5 rounded-full hover:bg-white/5 transition-colors text-muted-foreground hover:text-foreground relative"
                            data-testid="cart-nav-btn"
                        >
                            <Tote size={18} />
                        </button>

                        {/* Theme toggle */}
                        <button
                            onClick={toggleTheme}
                            className="p-1.5 rounded-full hover:bg-white/5 transition-colors text-muted-foreground hover:text-foreground"
                            data-testid="theme-toggle"
                            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                        </button>

                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full neon-border bg-white/5">
                            <span className="font-mono text-sm font-bold text-neon-gold text-glow-gold">
                                {user?.boltz_balance?.toFixed(2) || '0.00'}
                            </span>
                            <span className="text-xs font-bold text-primary" style={{ fontFamily: 'var(--font-display)' }}>Boltz</span>
                        </div>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className="flex items-center gap-2 p-1 rounded-full hover:bg-white/5 transition-colors"
                                    data-testid="user-menu-trigger"
                                >
                                    <Avatar className="h-8 w-8 border-2 border-primary shadow-[0_0_10px_rgba(30,144,255,0.3)]">
                                        <AvatarImage src={user?.picture} alt={user?.name} />
                                        <AvatarFallback className="bg-primary text-primary-foreground text-sm font-bold" style={{ fontFamily: 'var(--font-display)' }}>
                                            {getInitials()}
                                        </AvatarFallback>
                                    </Avatar>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56 bg-card border-border">
                                <div className="px-3 py-2">
                                    <p className="font-medium text-foreground">{user?.name || user?.email}</p>
                                    <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={() => navigate('/profile')}
                                    data-testid="menu-profile"
                                >
                                    <User className="mr-2 h-4 w-4" />
                                    Profile
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate('/wallet')} data-testid="menu-wallet">
                                    <Wallet className="mr-2 h-4 w-4" />
                                    Wallet
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate('/connect')} data-testid="menu-connect"
                                    className="text-boltz-gold focus:text-boltz-gold">
                                    <Storefront className="mr-2 h-4 w-4" weight="fill" />
                                    Seller Hub
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate('/referrals')} data-testid="menu-referrals"
                                    className="text-hot-pink focus:text-hot-pink">
                                    <Users className="mr-2 h-4 w-4" weight="fill" />
                                    Refer & Earn
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate('/alerts')} data-testid="menu-alerts">
                                    <Bell className="mr-2 h-4 w-4" />
                                    Price Alerts
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate('/badges')} data-testid="menu-badges"
                                    className="text-neon-gold focus:text-neon-gold">
                                    <Medal className="mr-2 h-4 w-4" weight="fill" />
                                    Badges
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate('/ride-history')}>
                                    <Clock className="mr-2 h-4 w-4" />
                                    Ride History
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate('/messages')}>
                                    <ChatDots className="mr-2 h-4 w-4" />
                                    Messages
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate('/billing')} data-testid="menu-billing"
                                    className="text-boltz-gold focus:text-boltz-gold">
                                    <Crown className="mr-2 h-4 w-4" weight="fill" />
                                    Membership
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate('/help')}>
                                    <Question className="mr-2 h-4 w-4" />
                                    Help Center
                                </DropdownMenuItem>
                                {isAdmin && (
                                    <DropdownMenuItem
                                        onClick={() => navigate('/admin')}
                                        className="text-primary focus:text-primary"
                                        data-testid="menu-admin"
                                    >
                                        <ShieldCheck className="mr-2 h-4 w-4" weight="fill" />
                                        Admin Dashboard
                                    </DropdownMenuItem>
                                )}
                                {isAdmin && (
                                    <DropdownMenuItem
                                        onClick={() => navigate('/admin-analytics')}
                                        className="text-cyber-purple focus:text-cyber-purple"
                                        data-testid="menu-admin-analytics"
                                    >
                                        <ChartLine className="mr-2 h-4 w-4" weight="fill" />
                                        Analytics
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={logout}
                                    className="text-destructive focus:text-destructive"
                                    data-testid="menu-logout"
                                >
                                    <SignOut className="mr-2 h-4 w-4" />
                                    Sign out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </div>
        </header>
    );
}
